import { SocketUpdateModel } from '../../components/smartsend/SmartSendSocketsModels';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendItemsCache } from '../cache/SmartSendItemsCache';
import { SumNumber } from '../helpers/ArrayHelper';
import {
  SmartSendBulkApplier,
  dimensionKeysToParcelFields,
  reflowConsignmentDimensions
} from '../helpers/SmartSendBulkActionApplier';
import { SmartSendBulkStatusApplier } from '../helpers/SmartSendBulkStatusApplier';
import { SanitiseStatus } from '../helpers/SanitiseStatus';
import SmartSendFilterApplier, { DefaultSummary, SmartSendFilterContext } from '../helpers/SmartSendFilterApplier';
import { SmartSendSingleActionApplier } from '../helpers/SmartSendSingleActionApplier';
import * as SSSH from '../helpers/SmartSendSocketHelper';
import SmartSendSoldItemsSummaryGenerator, {
  IsDomestic,
  IsInternational,
  IsReturnCollection,
  IsSavedForLater,
  IsSmartSend,
  ItemShouldAllowCustomerUpdate,
  SmartSendHasSource
} from '../helpers/SmartSendSummaryGenerator';
import { IAppContextAction } from './BaseContextModels';
import {
  stateCustomerIsSender,
  initialiseItem,
  mapCustomer,
  mapSenderToItem,
  sanitiseItemForSave
} from './ContextFunctions';
import {
  BulkSaveSelectedAction,
  DefaultSmartSendItemState,
  ISmartSendContext,
  SaveItemsAction,
  SmartSendKeyedState
} from './SmartSendContextModels';

import { SmartSendSubType } from '../consts/SmartSendSubType';
import { SmartSendBulkExtrasApplier } from '../helpers/SmartSendBulkExtrasApplier';
import { IsPackaged } from '../helpers/IsPackaged';
import ItemIsNotReady from '../helpers/ItemIsNotReady';
import ItemIsReady from '../helpers/ItemIsReady';
import { applySmartSendFilterContext } from '../helpers/DefaultFiltersPreferences';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { captureException } from '~/lib/metrics';

/**
 * Regenerates the summaries for every filter tab. Almost every case ends with this; the only ones
 * that pass a resetSelection are SET_QUOTE_PROVIDERS and BULK_ACTION_STATUS.
 */
const summarise = (state: ISmartSendContext, resetSelection = false): ISmartSendContext =>
  SmartSendSoldItemsSummaryGenerator(
    state,
    state.filters[SmartSendSubType].filter,
    resetSelection,
    SmartSendItemsCache
  );

/**
 * Drops every cached item belonging to a tab. Rebuilds in place because SmartSendItemsCache is a
 * module-level const that the rest of the app holds by reference - it cannot be reassigned.
 */
const removeCachedItemsForTab = (tab: string) => {
  const kept = SmartSendItemsCache.filter((x) => x.SubType !== tab);
  if (kept.length === SmartSendItemsCache.length) {
    return;
  }
  SmartSendItemsCache.length = 0;
  for (const item of kept) {
    SmartSendItemsCache.push(item);
  }
};

/**
 * Adds an item to the cache unless it is already there, mapping the customer onto it first.
 * `knownIds` is mutated as items are added so a batch does not add the same id twice.
 */
const addItemToCacheIfNew = (i: SmartSendItemApiModel, state: ISmartSendContext, knownIds: Set<string>) => {
  if (knownIds.has(i.Id)) {
    return;
  }
  initialiseItem(i);
  if (IsReturnCollection(i)) {
    i.Recipient = mapCustomer(i.Recipient, state.returnCustomer);
  } else {
    i.Sender = mapCustomer(i.Sender, state.customer);
    mapSenderToItem(i, state.customer);
  }
  if (!i.State) {
    i.State = { ...DefaultSmartSendItemState(), state: 'NEW' };
  }
  SmartSendItemsCache.push({ ...i, SubType: SmartSendSubType });
  knownIds.add(i.Id);
};

const cachedIds = () => new Set(SmartSendItemsCache.map((x) => x.Id));

const SmartSendContextReducer = (state: ISmartSendContext, action: IAppContextAction): ISmartSendContext => {
  switch (action.type) {
    case 'SET_LISTY_ITEMS': {
      const payload = action.payload as {
        items: SmartSendItemApiModel[];
        tab: string;
        take: number;
        skip: number;
      };
      const newState = { ...state };

      // Remove all existing items of the same type
      removeCachedItemsForTab(action.payload.tab);

      // Add the new items to the cache so we can use them in the Edit features
      action.payload.items.forEach((item: SmartSendItemApiModel) => {
        initialiseItem(item);
        if (!item.State) {
          item.State = { ...DefaultSmartSendItemState(), state: 'IDLE' };
        }
        SmartSendItemsCache.push({ ...item, SubType: action.payload.tab });
      });

      // Store for filter
      newState.filters[payload.tab].filter.CurrentFilter.Skip = payload.skip;
      newState.filters[payload.tab].filter.CurrentFilter.Take = payload.take;

      // Return state with the new items
      return summarise(newState);
    }

    case 'SET_PACKINGLISTSEARCH_ITEMS': {
      removeCachedItemsForTab(action.payload.tab);
      action.payload.items.forEach((item: SmartSendItemApiModel) => {
        initialiseItem(item);
        if (!item.State) {
          item.State = { ...DefaultSmartSendItemState(), state: 'IDLE' };
        }
        SmartSendItemsCache.push({ ...item, SubType: action.payload.tab });
      });
      return { ...state };
    }

    case 'UPDATE_FILTER': {
      const newState = { ...state };
      const payload = action.payload as { filterKey: string; filter: SmartSendFilterContext };
      applySmartSendFilterContext(action.payload.filter);
      newState.filters[payload.filterKey].filter = payload.filter;
      return summarise(newState);
    }

    case 'CREATE_FILTER': {
      const newState = { ...state };
      const payload = action.payload as { filterKey: string; filter: SmartSendFilterContext };
      applySmartSendFilterContext(payload.filter);
      newState.filters[payload.filterKey] = {
        filter: payload.filter,
        summary: DefaultSummary()
      };
      return newState;
    }

    case 'SET_QUOTE_PROVIDERS': {
      const newState = { ...state };
      newState.filters[SmartSendSubType].filter.CurrentFilter.FilterSelections.forEach((f) => {
        f.Include = [];
        if (f.Name === 'MarketplaceStatus') {
          f.Include = ['Sold'];
        }
        if (f.Name === 'Status') {
          f.Include = ['Active', 'Inactive'];
        }
      });
      return summarise(newState, true);
    }

    case 'ADD_NEW_ITEM': {
      const newState = { ...state };
      const i = action.payload.item as SmartSendItemApiModel;
      addItemToCacheIfNew(i, newState, cachedIds());
      return summarise(newState);
    }

    case 'ADD_NEW_ITEMS': {
      const newState = { ...state };
      const knownIds = cachedIds();
      action.payload.items.forEach((i: SmartSendItemApiModel) => addItemToCacheIfNew(i, newState, knownIds));
      return summarise(newState);
    }

    case 'SAVE_ITEMS': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);

      // Bulk Update Customers if the main one has been changed. Only do for single item updates.
      let customerHasBeenUpdated = false;
      if (
        SettingsCache.settings.Features.includes('UpdateCustomerOnSenderUpdate') &&
        action.payload.actions.includes('Sender') &&
        action.payload.ids.length === 1
      ) {
        const newCustomer = SmartSendItemsCache.find((x) => ids.has(x.Id))?.Sender;
        if (newCustomer && stateCustomerIsSender(newState.customer, newCustomer)) {
          newState.customer = newCustomer;
          customerHasBeenUpdated = true;
        }
      }

      SmartSendItemsCache.forEach((i) => {
        // Update customer
        if (customerHasBeenUpdated && ItemShouldAllowCustomerUpdate(i)) {
          i.Sender = mapCustomer(i.Sender, newState.customer);
          mapSenderToItem(i, newState.customer);
          i.State.state = 'INITIAL';
        }

        if (ids.has(i.Id)) {
          const unsaved = SmartSendSingleActionApplier(i, action as SaveItemsAction);
          if (unsaved.length > 0) {
            i.State.unsaved = unsaved;
            i.State.state = 'SAVE';
            sanitiseItemForSave(i);
          }
        }
      });

      return summarise(newState);
    }

    case 'RELEASE_BOOKING': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          i.BookingDetails = null;
          i.MarketplaceStatus = 'Sold';
          i.State.unsaved = ['BookingDetails_Release'];
          i.State.state = 'SAVE';
        }
      });
      return summarise(newState);
    }

    case 'MARK_AS_PAID_TEMP': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      // Quick UI Update - The Booking Details will come down in the sockets
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          i.MarketplaceStatus = 'AwaitingDispatch';
          i.BookingDetails = {
            BookingStatus: 'Booked',
            Service: i.Service,
            Created: new Date()
          };
        }
      });
      return summarise(newState);
    }

    case 'MARK_AS_PAID': {
      const newState = { ...state };
      const paidItems = new Map<string, SmartSendItemApiModel>();
      // First entry wins, matching the find() this replaced.
      action.payload.items.forEach((p: SmartSendItemApiModel) => {
        if (!paidItems.has(p.Id)) {
          paidItems.set(p.Id, p);
        }
      });
      // Quick UI Update - The Booking Details will come down in the sockets
      SmartSendItemsCache.forEach((i) => {
        const item = paidItems.get(i.Id);
        if (item) {
          i.MarketplaceStatus = 'AwaitingDispatch';
          i.BookingDetails = item.BookingDetails || {
            BookingStatus: 'Booked',
            Service: i.Service,
            Created: new Date()
          };
        }
      });
      return summarise(newState);
    }

    case 'MARK_AS_STAGED': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      // Quick UI Update - The Booking Details will come down in the sockets
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          i.BookingDetails = { BookingStatus: 'AwaitingPayment', Created: new Date() };
        }
      });
      return summarise(newState);
    }

    case 'MARK_PACKAGED': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          i.Meta['PackagedDate'] = { Value: action.payload.packaged ? new Date().toString() : '', Type: 'DateTime' };
          i.State.unsaved = [action.payload.packaged ? 'MarkAsPackaged' : 'MarkAsNotPackaged'];
          i.State.state = 'SAVE';
        }
      });

      return summarise(newState);
    }

    case 'MARK_DISPATCHED': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          // if this is a retry the status wants to be 'PendingRetry' for the UI to update
          i.MarketplaceStatus = action.payload.isRetry === true ? 'PendingRetry' : 'Dispatched';
          i.State.unsaved = ['MarkAsDispatched', 'MarketplaceStatus'];
          i.State.state = 'SAVE';
        }
      });
      return summarise(newState);
    }

    case 'MARK_ALL_PACKAGED': {
      const newState = { ...state };
      SmartSendItemsCache.filter(
        (i) => IsSmartSend(i) && i.Status === 'Active' && i.MarketplaceStatus === 'AwaitingDispatch' && !IsPackaged(i)
      ).forEach((i) => {
        i.Meta['PackagedDate'] = { Value: new Date().toString(), Type: 'DateTime' };
        i.State.unsaved = ['MarkAsPackaged'];
        i.State.state = 'SAVE';
      });
      return summarise(newState);
    }

    case 'ITEM_ACTION_STATUS': {
      const newState = { ...state };
      SmartSendItemsCache.forEach((i) => {
        if (i.Id === action.payload.id) {
          i.Status = SanitiseStatus(action.payload.status);
          i.State.state = 'SAVE';
          i.State.selected = !!action.payload.select;
          i.State.unsaved = ['Status'];
        }
      });
      return summarise(newState);
    }

    case 'REMOVE_TAG': {
      const newState = { ...state };
      SmartSendItemsCache.forEach((i) => {
        if (i.Id === action.payload.id) {
          i.Tags = i.Tags.filter((x) => x !== action.payload.tag);
          i.State.state = 'SAVE';
          i.State.unsaved = ['Tags'];
        }
      });
      return summarise(newState);
    }

    case 'SAVE_ITEM_TAGS': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.forEach((i) => {
        if (ids.has(i.Id)) {
          i.Tags = action.payload.tags.sort();
          i.State.state = 'SAVE';
          i.State.unsaved = ['Tags'];
        }
      });
      return summarise(newState);
    }

    case 'UPDATE_CUSTOMER': {
      const newState = { ...state };
      if (action.payload.customer) {
        newState.customer = action.payload.customer;
        SmartSendItemsCache.forEach((i) => {
          if (ItemShouldAllowCustomerUpdate(i)) {
            i.Sender = mapCustomer(i.Sender, newState.customer);
            mapSenderToItem(i, newState.customer);
            i.State.state = 'INITIAL';
          }
        });
      }
      return summarise(newState);
    }

    case 'INITIALISE_CUSTOMER': {
      if (action.payload.customer) {
        return {
          ...state,
          customer: { ...action.payload.customer },
          returnCustomer: { ...action.payload.customer },
          customerInitialised: true
        };
      }
      return { ...state };
    }

    case 'BULK_SAVE_SELECTED_ITEMS': {
      const newState = { ...state };
      SmartSendBulkApplier(action.payload.filterKey, newState, action as BulkSaveSelectedAction, SmartSendItemsCache);
      return summarise(newState);
    }

    case 'BULK_SAVE_EXTRAS': {
      const newState = { ...state };
      SmartSendBulkExtrasApplier(SmartSendItemsCache, action.payload.ids, action.payload.type, action.payload.extras);
      return summarise(newState);
    }

    case 'RESET_SELECTION': {
      const newState = { ...state };
      const payload = action.payload as { filterKey: string };
      const selected = new Set<string>(newState.filters[payload.filterKey].summary.selected);
      newState.filters[payload.filterKey].summary.selected = [];
      SmartSendItemsCache.filter((x) => selected.has(x.Id)).forEach((x) => {
        x.State.selected = false;
      });
      return newState;
    }

    case 'BULK_ACTION_STATUS': {
      const newState = { ...state };
      SmartSendBulkStatusApplier(action.payload.filterKey, newState, action.payload.status, SmartSendItemsCache);
      return summarise(newState, !!action.payload.resetSelection);
    }

    // Changed the State of an item. Idle,Quoting,Error etc
    case 'UPDATE_ITEM_STATE': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.filter((x) => ids.has(x.Id)).forEach((x) => {
        x.State.state = action.payload.to;
      });
      return newState;
    }

    case 'ARRANGE_RETURN_COLLECTION': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload);
      SmartSendItemsCache.filter((x) => ids.has(x.Id)).forEach((x) => {
        x.State.state = 'RETURN';
      });
      return newState;
    }

    case 'TOGGLE_GROUP': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.filter((x) => ids.has(x.Id)).forEach((x) => {
        x.State.selected = !!action.payload.val as boolean;
      });
      return summarise(newState);
    }

    case 'TOGGLE_GROUP_WITH_ACTION': {
      const newState = { ...state };
      const ids = new Set<string>(action.payload.ids);
      SmartSendItemsCache.filter((x) => ids.has(x.Id)).forEach((x) => {
        x.State.selected = !!action.payload.val as boolean;
      });
      newState.deferredAction = newState.deferredAction || {};
      newState.deferredAction[SmartSendSubType] = action.payload.action;
      return summarise(newState);
    }

    case 'CLEAR_DEFERRED_ACTION': {
      const newState = { ...state };
      newState.deferredAction[action.payload.filterKey] = undefined;
      return newState;
    }

    case 'TOGGLE_ALL': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          i.State.selected = !!payload.value as boolean;
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_SOURCE': {
      const newState = { ...state };
      const payload = action.payload as { source: string; value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (SmartSendHasSource(i, payload.source)) {
            i.State.selected = payload.value;
          }
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_PROVIDER': {
      const newState = { ...state };
      const payload = action.payload as { provider: string; value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (i.Service.Provider === payload.provider) {
            i.State.selected = payload.value;
          }
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_SAVEDFORLATER': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (IsSavedForLater(i)) {
            i.State.selected = payload.value;
          }
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_READY': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (ItemIsReady(i)) {
            i.State.selected = !!payload.value as boolean;
          }
        });
      });
      return summarise(newState);
    }

    case 'SEND_READY': {
      const newState = { ...state };
      const payload = action.payload as { filter: SmartSendFilterContext; filterKey: string };

      // Wipe all issues from the filter
      payload.filter.CurrentFilter.FilterSelections.forEach((f) => {
        if (f.Name === 'Issues') {
          f.Include = [];
          f.Exclude = [];
        }
      });

      // Mark all 'Ready' items as selected
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (ItemIsReady(i)) {
            i.State.selected = true;
          }
        });
      });

      // Set the filter to be the main display (covers scenarios where the filters arent visible)
      applySmartSendFilterContext(action.payload.filter);
      newState.filters[payload.filterKey].filter = payload.filter;

      return summarise(newState);
    }

    case 'TOGGLE_INTERNATIONAL': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.filter(IsInternational).forEach((i) => {
          if (IsInternational(i)) {
            i.State.selected = !!payload.value as boolean;
          }
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_DOMESTIC': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (IsDomestic(i)) {
            i.State.selected = !!payload.value as boolean;
          }
        });
      });
      return summarise(newState);
    }

    case 'TOGGLE_NOTREADY': {
      const newState = { ...state };
      const payload = action.payload as { value: boolean; filterKey: string };
      SmartSendFilterApplier(SmartSendItemsCache, newState.filters[payload.filterKey].filter).forEach((g) => {
        g.Items.forEach((i) => {
          if (ItemIsNotReady(i)) {
            i.State.selected = !!payload.value as boolean;
          }
        });
      });
      return summarise(newState);
    }

    case 'UPDATE_ITEM_CACHE': {
      const newState = { ...state };
      SmartSendItemsCache.filter((x) => action.payload.key === x.Id).forEach((x) => {
        x.State = { ...x.State, ...action.payload.value };
      });

      return summarise(newState);
    }

    case 'UPDATE_ITEMS_CACHE': {
      const newState = { ...state };
      // Grouped by id, not a plain id -> item map: the same item can sit in the cache more than
      // once under different SubTypes, and every copy needs the update.
      const byId = new Map<string, SmartSendItemApiModel[]>();
      SmartSendItemsCache.forEach((x) => {
        const existing = byId.get(x.Id);
        if (existing) {
          existing.push(x);
        } else {
          byId.set(x.Id, [x]);
        }
      });
      action.payload.items.forEach((i: SmartSendKeyedState) => {
        (byId.get(i.key) || []).forEach((x) => {
          x.State = { ...x.State, ...i.value };

          // Fix for CourierServices
          if (!x.Service?.Name && !!x.State?.validation?.QuoteState?.Quote?.Service?.Name) {
            x.Service.Name = x.State.validation.QuoteState.Quote.Service.Name;
          }
        });
      });

      return summarise(newState);
    }

    case 'SOCKET:SMARTSEND_ITEMS_UPDATED': {
      const newState = { ...state };
      const data = action.payload.model as SocketUpdateModel;

      data.Items.forEach((update) => {
        SmartSendItemsCache.forEach((i) => {
          if (update.Id === i.Id) {
            let quote = false;
            try {
              for (const changed in update.ItemUpdates) {
                // Other User changes
                // let itemId = update.Id;
                const val = update.ItemUpdates[changed];
                switch (changed) {
                  case 'Dimensions':
                    quote = quote || SSSH.DimensionChangeShouldTriggerQuote(i, val);
                    Object.assign(i.PackageDimensions, val);
                    // Same gap the bulk path had before step 7: without this the parcels keep their
                    // old dimensions, so GetItemWeight (which sums parcels) ignores the update and the
                    // API books the stale weights. Only the axes actually present in the payload.
                    reflowConsignmentDimensions(i, dimensionKeysToParcelFields(val));
                    break;
                  case 'MarketplaceStatus':
                    i.MarketplaceStatus = val;
                    break;
                  case 'DispatchStatus':
                    // Failed to mark as dispatched
                    // TODO
                    break;
                  case 'BookingDetails':
                    if (val === null) {
                      i.BookingDetails = val;
                    } else {
                      Object.assign(i.BookingDetails, val);
                    }
                    break;
                  case 'TrackingSummary':
                    try {
                      if (new Date(i.TrackingSummary.LatestStatus) < new Date(val.LatestStatus)) {
                        Object.assign(i.TrackingSummary, val);
                      }
                    } catch {
                      i.TrackingSummary = val;
                    }
                    break;
                  case 'Status':
                    quote = quote || SSSH.StatusChangeShouldTriggerQuote(i, val);
                    i.Status = val;
                    break;
                  case 'Recipient':
                    quote = quote || SSSH.RecipientChangeShouldTriggerQuote(i, val);
                    Object.assign(i.Recipient, val);
                    break;
                  case 'RecipientContactDetails':
                    i.Recipient.Email = val.Email || i.Recipient.Email;
                    i.Recipient.Phone = val.Phone || i.Recipient.Phone;
                    break;
                  case 'Meta':
                    if (val) {
                      for (const p in val) {
                        i.Meta[p] = val[p];
                      }
                    }
                    break;
                  case 'Consignment':
                    if (val) {
                      // Parcel count and per-parcel dimensions are the primary price inputs for a
                      // multi-parcel shipment, but this case used to replace the consignment without
                      // setting `quote` - unlike every other price-affecting case here. So another
                      // session rearranging the parcels left this one showing a price for the old
                      // arrangement, with nothing to indicate it was stale.
                      quote = quote || SSSH.ConsignmentChangeShouldTriggerQuote(i, val);
                      i.Consignment = val;
                    }
                    break;
                  case 'MarketplaceOrderLines':
                    quote = quote || SSSH.MarketplaceOrderLinesChangeShouldTriggerQuote(i, val);
                    i.MarketplaceOrderLines = val;
                    i.Value = SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
                    break;
                  case 'Owners':
                    if (SSSH.OwnersHaveChanged(i.Owners, val)) {
                      i.Owners = val;
                    }
                    break;
                  case 'Tags':
                    if (SSSH.TagsHaveChanged(i, val)) {
                      i.Tags = val;
                    }
                    break;
                  case 'Sender':
                    // Intentionally not applied. Was previously a commented-out mapCustomer call;
                    // left as an explicit no-op so the case is not mistaken for an omission.
                    break;
                  case 'Service':
                    quote = quote || SSSH.ServiceChangeShouldTriggerQuote(i, val);
                    i.Service = val;
                    break;
                  case 'Value':
                    quote = quote || SSSH.ValueChangeShouldTriggerQuote(i, val);
                    i.Value = val || SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
                    break;
                }
              }
            } catch (e) {
              // Socket payloads are not schema-guaranteed, so one bad update must not take out the
              // rest of the batch. Reported rather than logged - this catch also covers genuine bugs
              // in the cases above, which used to disappear silently.
              captureException(e, 'Failed to apply a SmartSend socket item update', {
                area: 'smartsend-sockets',
                itemId: update.Id
              });
            }
            if (quote) {
              i.State.state = 'QUOTE';
            }
            // Record update
            i.Updated = new Date();
            i.UpdatedBy = i.UpdatedBy || 'Sockets';
          }
        });
      });

      return summarise(newState);
    }

    case 'SOCKET:SMARTSEND_NEW_ITEMS': {
      const newState = { ...state };
      const knownIds = cachedIds();
      action.payload.items.forEach((i: SmartSendItemApiModel) => addItemToCacheIfNew(i, newState, knownIds));
      return summarise(newState);
    }

    case 'SOCKET:SMARTSEND_REMOVE_ITEMS': {
      const newState = { ...state };
      action.payload.items.forEach((i: SmartSendItemApiModel) => {
        const existingItem = SmartSendItemsCache.find((x) => x.Id === i.Id);
        if (existingItem) {
          const index = SmartSendItemsCache.indexOf(existingItem);
          SmartSendItemsCache.splice(index, 1);
        }
      });
      return summarise(newState);
    }
  }

  return state;
};

export default SmartSendContextReducer;
